/**
 * GenerationPreviewModal Component
 *
 * Modal that displays extracted resources from auto-generation for review.
 * Supports creating new resources and updating existing ones (matched by name),
 * with diff highlighting for changed fields and optional removal of orphan attributes.
 */

import { useState, useEffect } from 'react';
import { apiPost, ApiHttpError, TENANT_TOKEN_KEY } from '../../../api/client';
import { Modal, Button } from '../../../design-system';
import { tokens } from '../../../design-system/tokens';
import type { ExtractedResource } from './AutoGenerateButton';
import {
  NEW_TYPE_VALUE,
  buildEditableResources,
  attributesToApply,
  orphanKeysToRemove,
  scalarFieldChanged,
  type EditableResource,
  type AttributeRow,
  type ExistingResourcePreview,
} from './generationPreviewMerge';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ResourceType {
  id: string;
  name: string;
}

interface BulkApplyResourceInput {
  id?: string;
  name: string;
  description: string | null;
  capacity: number;
  resourceTypeId: string | null;
  newTypeName: string | null;
  customAttributes?: Record<string, string> | null;
  removeAttributeKeys?: string[];
}

export interface GenerationPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  resources: ExtractedResource[];
  existingResources: ExistingResourcePreview[];
  resourceTypes: ResourceType[];
  agentId: string;
  onApplied: (created: number, updated: number) => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function GenerationPreviewModal({
  isOpen,
  onClose,
  resources,
  existingResources,
  resourceTypes,
  agentId,
  onApplied,
}: GenerationPreviewModalProps) {
  const [editableResources, setEditableResources] = useState<EditableResource[]>([]);
  const [loading, setLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ index: number; field: string; message: string }[]>([]);

  useEffect(() => {
    if (isOpen && resources.length > 0) {
      setEditableResources(buildEditableResources(resources, existingResources, resourceTypes));
      setGeneralError('');
      setFieldErrors([]);
    }
  }, [isOpen, resources, existingResources, resourceTypes]);

  const updateResource = (index: number, field: keyof EditableResource, value: string | number) => {
    setEditableResources((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    setFieldErrors((prev) => prev.filter((e) => !(e.index === index && e.field === field)));
  };

  const updateAttribute = (
    resourceIndex: number,
    attrIndex: number,
    patch: Partial<Pick<AttributeRow, 'key' | 'proposedValue' | 'markForDeletion'>>
  ) => {
    setEditableResources((prev) => {
      const updated = [...prev];
      const attrs = [...updated[resourceIndex].attributes];
      attrs[attrIndex] = { ...attrs[attrIndex], ...patch };
      updated[resourceIndex] = { ...updated[resourceIndex], attributes: attrs };
      return updated;
    });
  };

  const addAttribute = (resourceIndex: number) => {
    setEditableResources((prev) => {
      const updated = [...prev];
      updated[resourceIndex] = {
        ...updated[resourceIndex],
        attributes: [
          ...updated[resourceIndex].attributes,
          { key: '', proposedValue: '', status: 'new', markForDeletion: false },
        ],
      };
      return updated;
    });
  };

  const removeResource = (index: number) => {
    setEditableResources((prev) => prev.filter((_, i) => i !== index));
    setFieldErrors((prev) =>
      prev
        .filter((e) => e.index !== index)
        .map((e) => (e.index > index ? { ...e, index: e.index - 1 } : e))
    );
  };

  const getFieldError = (index: number, field: string): string | undefined => {
    return fieldErrors.find((e) => e.index === index && e.field === field)?.message;
  };

  const createCount = editableResources.filter((r) => r.mode === 'create').length;
  const updateCount = editableResources.filter((r) => r.mode === 'update').length;

  const handleConfirm = async () => {
    if (editableResources.length === 0) return;

    const token = localStorage.getItem(TENANT_TOKEN_KEY);
    if (!token) return;

    setGeneralError('');
    setFieldErrors([]);
    setLoading(true);

    try {
      const payload: BulkApplyResourceInput[] = editableResources.map((r) => ({
        id: r.mode === 'update' ? r.existingResourceId : undefined,
        name: r.name.trim(),
        description: r.description.trim() || null,
        capacity: r.capacity,
        resourceTypeId: r.selectedTypeId && r.selectedTypeId !== NEW_TYPE_VALUE ? r.selectedTypeId : null,
        newTypeName:
          !r.selectedTypeId || r.selectedTypeId === NEW_TYPE_VALUE ? r.suggestedTypeName : null,
        customAttributes: attributesToApply(r.attributes),
        removeAttributeKeys: r.mode === 'update' ? orphanKeysToRemove(r.attributes) : undefined,
      }));

      const data = await apiPost<{ created: unknown[]; updated: unknown[]; typesCreated: number }>(
        `/api/agents/${agentId}/calendar/resources/bulk-create`,
        { resources: payload },
        token,
        { showErrorGrowl: false }
      );

      onApplied(data.created?.length ?? 0, data.updated?.length ?? 0);
      onClose();
    } catch (err) {
      if (err instanceof ApiHttpError && err.status === 400) {
        try {
          const parsed = JSON.parse(err.message);
          if (Array.isArray(parsed)) {
            setFieldErrors(
              parsed.map((e: { index?: number; field?: string; message?: string }) => ({
                index: e.index ?? 0,
                field: e.field ?? 'name',
                message: e.message ?? 'Error de validación',
              }))
            );
          } else {
            setGeneralError(err.message);
          }
        } catch {
          setGeneralError(err.message || 'Error de validación en los recursos.');
        }
      } else if (err instanceof Error) {
        setGeneralError(err.message || 'Ocurrió un error al aplicar los cambios.');
      } else {
        setGeneralError('Ocurrió un error inesperado.');
      }
    } finally {
      setLoading(false);
    }
  };

  const confirmLabel = () => {
    if (loading) return 'Aplicando...';
    if (createCount > 0 && updateCount > 0) {
      return `Crear ${createCount} y actualizar ${updateCount}`;
    }
    if (updateCount > 0) return `Actualizar ${updateCount} recurso${updateCount === 1 ? '' : 's'}`;
    return 'Confirmar creación';
  };

  const isEmpty = resources.length === 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Recursos detectados"
      size="lg"
      closeOnOverlayClick={!loading}
      closeOnEscape={!loading}
      footer={
        !isEmpty ? (
          <div style={{ display: 'flex', gap: tokens.spacing[3] }}>
            <Button variant="ghost" size="md" onClick={onClose} disabled={loading}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleConfirm}
              disabled={loading || editableResources.length === 0}
            >
              {confirmLabel()}
            </Button>
          </div>
        ) : (
          <Button variant="ghost" size="md" onClick={onClose}>
            Cerrar
          </Button>
        )
      }
    >
      {isEmpty && (
        <div
          style={{
            textAlign: 'center',
            padding: tokens.spacing[8],
            color: tokens.colors.neutral[500],
          }}
        >
          <p style={{ margin: 0, fontSize: tokens.typography.fontSize.base }}>
            No se encontraron recursos reservables en el contexto del agente
          </p>
        </div>
      )}

      {generalError && (
        <div
          style={{
            padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
            marginBottom: tokens.spacing[4],
            borderRadius: tokens.borderRadius.md,
            backgroundColor: tokens.colors.error.light,
            color: tokens.colors.error.dark,
            fontSize: tokens.typography.fontSize.sm,
          }}
        >
          {generalError}
        </div>
      )}

      {!isEmpty && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[4] }}>
          {(createCount > 0 || updateCount > 0) && (
            <p style={{ margin: 0, fontSize: tokens.typography.fontSize.sm, color: tokens.colors.neutral[600] }}>
              {createCount > 0 && (
                <span>
                  {createCount} nuevo{createCount === 1 ? '' : 's'}
                </span>
              )}
              {createCount > 0 && updateCount > 0 && ' · '}
              {updateCount > 0 && (
                <span>
                  {updateCount} actualización{updateCount === 1 ? '' : 'es'} (mismo nombre)
                </span>
              )}
            </p>
          )}

          {editableResources.length === 0 && (
            <div
              style={{
                textAlign: 'center',
                padding: tokens.spacing[6],
                color: tokens.colors.neutral[500],
                fontSize: tokens.typography.fontSize.sm,
              }}
            >
              Se eliminaron todos los recursos de la lista. Cancela para volver atrás.
            </div>
          )}

          {editableResources.map((resource, index) => (
            <ResourceCard
              key={`${resource.mode}-${resource.existingResourceId ?? resource.name}-${index}`}
              resource={resource}
              index={index}
              resourceTypes={resourceTypes}
              loading={loading}
              fieldErrorName={getFieldError(index, 'name')}
              fieldErrorCapacity={getFieldError(index, 'capacity')}
              onUpdate={(field, value) => updateResource(index, field, value)}
              onRemove={() => removeResource(index)}
              onUpdateAttribute={(attrIndex, patch) => updateAttribute(index, attrIndex, patch)}
              onAddAttribute={() => addAttribute(index)}
            />
          ))}
        </div>
      )}
    </Modal>
  );
}

// ─── Resource card ───────────────────────────────────────────────────────────

function ResourceCard({
  resource,
  index,
  resourceTypes,
  loading,
  fieldErrorName,
  fieldErrorCapacity,
  onUpdate,
  onRemove,
  onUpdateAttribute,
  onAddAttribute,
}: {
  resource: EditableResource;
  index: number;
  resourceTypes: ResourceType[];
  loading: boolean;
  fieldErrorName?: string;
  fieldErrorCapacity?: string;
  onUpdate: (field: keyof EditableResource, value: string | number) => void;
  onRemove: () => void;
  onUpdateAttribute: (attrIndex: number, patch: Partial<AttributeRow>) => void;
  onAddAttribute: () => void;
}) {
  const descChanged = scalarFieldChanged(
    resource.mode,
    resource.description,
    resource.existingDescription
  );
  const capChanged = scalarFieldChanged(resource.mode, resource.capacity, resource.existingCapacity);

  return (
    <div
      style={{
        border: `1px solid ${resource.mode === 'update' ? tokens.colors.primary[200] : tokens.colors.neutral[200]}`,
        borderRadius: tokens.borderRadius.md,
        padding: tokens.spacing[4],
        position: 'relative',
        backgroundColor: resource.mode === 'update' ? tokens.colors.primary[50] : undefined,
      }}
    >
      <button
        type="button"
        onClick={onRemove}
        disabled={loading}
        style={{
          position: 'absolute',
          top: tokens.spacing[2],
          right: tokens.spacing[2],
          background: 'none',
          border: 'none',
          cursor: loading ? 'not-allowed' : 'pointer',
          color: tokens.colors.neutral[400],
          padding: tokens.spacing[1],
        }}
        aria-label={`Quitar ${resource.name || `recurso ${index + 1}`} de la lista`}
        title="Quitar de la lista"
      >
        <IconX />
      </button>

      <div style={{ marginBottom: tokens.spacing[3], paddingRight: tokens.spacing[6] }}>
        <ModeBadge mode={resource.mode} />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: tokens.spacing[3],
          paddingRight: tokens.spacing[6],
        }}
      >
        <Field label="Nombre" fullWidth error={fieldErrorName}>
          <input
            type="text"
            value={resource.name}
            onChange={(e) => onUpdate('name', e.target.value)}
            disabled={loading}
            style={inputStyle(!!fieldErrorName)}
          />
        </Field>

        <Field label="Descripción" fullWidth changed={descChanged}>
          <input
            type="text"
            value={resource.description}
            onChange={(e) => onUpdate('description', e.target.value)}
            disabled={loading}
            placeholder="Sin descripción"
            style={inputStyle(false, descChanged)}
          />
          {descChanged && resource.existingDescription != null && (
            <DiffHint label="Actual" value={resource.existingDescription || '(vacío)'} />
          )}
        </Field>

        <Field label="Capacidad" error={fieldErrorCapacity} changed={capChanged}>
          <input
            type="number"
            min={1}
            value={resource.capacity}
            onChange={(e) => onUpdate('capacity', Math.max(1, parseInt(e.target.value, 10) || 1))}
            disabled={loading}
            style={inputStyle(!!fieldErrorCapacity, capChanged)}
          />
          {capChanged && resource.existingCapacity != null && (
            <DiffHint label="Actual" value={String(resource.existingCapacity)} />
          )}
        </Field>

        <Field label="Tipo de recurso">
          <select
            value={resource.selectedTypeId}
            onChange={(e) => onUpdate('selectedTypeId', e.target.value)}
            disabled={loading}
            style={inputStyle(false)}
          >
            <option value={NEW_TYPE_VALUE}>✨ Nuevo: {resource.suggestedTypeName}</option>
            {resourceTypes.map((rt) => (
              <option key={rt.id} value={rt.id}>
                {rt.name}
              </option>
            ))}
          </select>
        </Field>

        <div style={{ gridColumn: '1 / -1' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: tokens.spacing[2],
            }}
          >
            <span
              style={{
                fontSize: tokens.typography.fontSize.xs,
                fontWeight: tokens.typography.fontWeight.medium,
                color: tokens.colors.neutral[600],
              }}
            >
              Atributos personalizados
            </span>
            <button
              type="button"
              onClick={onAddAttribute}
              disabled={loading}
              style={{
                fontSize: tokens.typography.fontSize.xs,
                color: tokens.colors.primary[600],
                background: 'none',
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              + Agregar atributo
            </button>
          </div>

          {resource.attributes.length === 0 && (
            <p style={{ margin: 0, fontSize: tokens.typography.fontSize.xs, color: tokens.colors.neutral[500] }}>
              Sin atributos detectados en el contexto
            </p>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[2] }}>
            {resource.attributes.map((attr, attrIndex) => (
              <AttributeRowEditor
                key={`${attr.key}-${attrIndex}`}
                row={attr}
                loading={loading}
                onChange={(patch) => onUpdateAttribute(attrIndex, patch)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function AttributeRowEditor({
  row,
  loading,
  onChange,
}: {
  row: AttributeRow;
  loading: boolean;
  onChange: (patch: Partial<AttributeRow>) => void;
}) {
  const isOrphan = row.status === 'orphan';
  const highlight =
    row.status === 'different' || row.status === 'new';

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: isOrphan ? '1fr 1fr auto' : '1fr 1fr auto',
        gap: tokens.spacing[2],
        alignItems: 'start',
        padding: tokens.spacing[2],
        borderRadius: tokens.borderRadius.sm,
        border: `1px solid ${
          isOrphan
            ? tokens.colors.warning.light
            : highlight
              ? tokens.colors.warning.main
              : tokens.colors.neutral[200]
        }`,
        backgroundColor: isOrphan
          ? tokens.colors.warning.light
          : highlight
            ? '#fffbeb'
            : tokens.colors.neutral[50],
        opacity: isOrphan && row.markForDeletion ? 0.55 : 1,
      }}
    >
      <input
        type="text"
        value={row.key}
        onChange={(e) => onChange({ key: e.target.value })}
        disabled={loading || isOrphan}
        placeholder="Clave"
        style={inputStyle(false)}
      />
      <div>
        <input
          type="text"
          value={row.proposedValue}
          onChange={(e) => onChange({ proposedValue: e.target.value })}
          disabled={loading || isOrphan}
          placeholder="Valor"
          style={inputStyle(false, row.status === 'different')}
        />
        {row.status === 'different' && row.existingValue != null && (
          <DiffHint label="Actual" value={row.existingValue} />
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, minWidth: 88 }}>
        <StatusChip status={row.status} />
        {isOrphan && (
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: tokens.typography.fontSize.xs,
              color: tokens.colors.error.dark,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={row.markForDeletion}
              onChange={(e) => onChange({ markForDeletion: e.target.checked })}
              disabled={loading}
            />
            Eliminar
          </label>
        )}
      </div>
    </div>
  );
}

function ModeBadge({ mode }: { mode: 'create' | 'update' }) {
  const isUpdate = mode === 'update';
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: tokens.typography.fontSize.xs,
        fontWeight: tokens.typography.fontWeight.semibold,
        padding: '2px 8px',
        borderRadius: tokens.borderRadius.full,
        backgroundColor: isUpdate ? tokens.colors.primary[100] : tokens.colors.success.light,
        color: isUpdate ? tokens.colors.primary[700] : tokens.colors.success.dark,
      }}
    >
      {isUpdate ? 'Actualizar existente' : 'Nuevo'}
    </span>
  );
}

function StatusChip({ status }: { status: AttributeRow['status'] }) {
  const labels: Record<AttributeRow['status'], string> = {
    new: 'Nuevo',
    same: 'Sin cambios',
    different: 'Diferente',
    orphan: 'No en contexto',
  };
  const colors: Record<AttributeRow['status'], { bg: string; fg: string }> = {
    new: { bg: tokens.colors.success.light, fg: tokens.colors.success.dark },
    same: { bg: tokens.colors.neutral[100], fg: tokens.colors.neutral[600] },
    different: { bg: '#fef3c7', fg: '#92400e' },
    orphan: { bg: tokens.colors.warning.light, fg: tokens.colors.warning.dark },
  };
  const c = colors[status];
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 600,
        padding: '2px 6px',
        borderRadius: 4,
        backgroundColor: c.bg,
        color: c.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {labels[status]}
    </span>
  );
}

function Field({
  label,
  children,
  fullWidth,
  error,
  changed,
}: {
  label: string;
  children: React.ReactNode;
  fullWidth?: boolean;
  error?: string;
  changed?: boolean;
}) {
  return (
    <div style={{ gridColumn: fullWidth ? '1 / -1' : undefined }}>
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: tokens.typography.fontSize.xs,
          fontWeight: tokens.typography.fontWeight.medium,
          color: tokens.colors.neutral[600],
          marginBottom: tokens.spacing[1],
        }}
      >
        {label}
        {changed && (
          <span style={{ color: '#b45309', fontWeight: 600, fontSize: 10 }}>modificado</span>
        )}
      </label>
      {children}
      {error && (
        <div style={{ fontSize: tokens.typography.fontSize.xs, color: tokens.colors.error.dark, marginTop: 4 }}>
          {error}
        </div>
      )}
    </div>
  );
}

function DiffHint({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        fontSize: tokens.typography.fontSize.xs,
        color: tokens.colors.neutral[500],
        marginTop: 4,
      }}
    >
      {label}: <span style={{ textDecoration: 'line-through' }}>{value}</span>
    </div>
  );
}

function inputStyle(hasError?: boolean, changed?: boolean): React.CSSProperties {
  return {
    width: '100%',
    padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
    fontSize: tokens.typography.fontSize.sm,
    borderRadius: tokens.borderRadius.md,
    border: `1px solid ${
      hasError
        ? tokens.colors.error.main
        : changed
          ? '#f59e0b'
          : tokens.colors.neutral[300]
    }`,
    backgroundColor: tokens.colors.neutral[50],
    color: tokens.colors.neutral[900],
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  };
}

function IconX() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default GenerationPreviewModal;
